import { useState, useEffect, useCallback } from "react";
import { Plus, Check, Trash2, CalendarDays, ChevronDown, ChevronRight, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useIsMobile } from "@/components/custom/AppShell";

const PRIORITIES = ["normal", "urgent", "low"];
const PRIORITY_COLORS = { urgent: "#DC2626", normal: "#F59E0B", low: "#9CA3AF" };

function fmtDue(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const now = new Date(); now.setHours(0,0,0,0);
    const due = new Date(d); due.setHours(0,0,0,0);
    const diff = Math.round((due - now) / 86400000);
    const label = `${String(d.getDate()).padStart(2,"0")} ${d.toLocaleDateString("en-GB", { month: "short" })}`;
    if (diff < 0) return { label, color: "#DC2626", tag: "Overdue" };
    if (diff === 0) return { label, color: "#D97706", tag: "Today" };
    if (diff === 1) return { label, color: "#D97706", tag: "Tomorrow" };
    return { label, color: "var(--text-muted)", tag: `${diff}d` };
  } catch { return { label: dateStr, color: "var(--text-muted)", tag: "" }; } 
}

function sortByDueDate(items) {
  return [...items].sort((a, b) => {
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  });
}

function capitalize(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function Todos() {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const ADMIN_USERS = ["Lucas Hammond", "Talia Cloutman", "Kayhan Hassan"];
  const [inputValues, setInputValues] = useState({});
  const [showCompleted, setShowCompleted] = useState({});
  const isMobile = useIsMobile();

  const currentUser = localStorage.getItem("desktop_user_name") || "";

  const fetchTodos = useCallback(async () => {
    try {
      const data = await api.get("/todos").then(r => r.data);
      setTodos(data);
    } catch (err) { console.error("Failed to load todos", err); }
  }, []);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const addTodo = async (assignedTo, text) => {
    if (!text.trim()) return;
    try {
      await api.post("/todos", { text: text.trim(), assigned_to: assignedTo, created_by: currentUser });
      fetchTodos();
    } catch { toast.error("Failed to add"); }
  };

  const completeTodo = async (id) => {
    try {
      await api.put(`/todos/${id}`, { completed: true });
      setTodos(prev => prev.map(t => t.id === id ? { ...t, completed: true, completed_at: new Date().toISOString() } : t));
    } catch { toast.error("Failed"); }
  };

  const uncompleteTodo = async (id) => {
    try {
      await api.put(`/todos/${id}`, { completed: false });
      setTodos(prev => prev.map(t => t.id === id ? { ...t, completed: false } : t));
    } catch { toast.error("Failed"); }
  };

  const cyclePriority = async (id, current) => {
    const idx = PRIORITIES.indexOf(current || "normal");
    const next = PRIORITIES[(idx + 1) % PRIORITIES.length];
    try {
      await api.put(`/todos/${id}`, { priority: next });
      setTodos(prev => prev.map(t => t.id === id ? { ...t, priority: next } : t));
    } catch { toast.error("Failed"); }
  };

  const updateTodoText = async (id, text) => {
    try { await api.put(`/todos/${id}`, { text }); setTodos(prev => prev.map(t => t.id === id ? { ...t, text } : t)); } catch { toast.error("Failed"); }
  };

  const updateTodoDueDate = async (id, due_date) => {
    try { await api.put(`/todos/${id}`, { due_date }); setTodos(prev => prev.map(t => t.id === id ? { ...t, due_date } : t)); } catch { toast.error("Failed"); }
  };

  const deleteTodo = async (id) => {
    try { await api.delete(`/todos/${id}`); fetchTodos(); } catch { toast.error("Failed"); }
  };

  const renderList = (allItems, assignedTo, inputVal, setInputVal) => {
    const active = sortByDueDate(allItems.filter(t => !t.completed));
    const completed = allItems.filter(t => t.completed);
    const isExpanded = showCompleted[assignedTo];

    return (
      <div className="ui-surface overflow-hidden flex-1">
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <h2 className="text-base font-semibold" style={{ color: "var(--text-primary)" }}>{assignedTo}</h2>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{active.length} active task{active.length !== 1 ? "s" : ""}</p>
          </div>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); addTodo(assignedTo, inputVal); setInputVal(""); }} className="flex gap-2 px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <Input value={inputVal} onChange={(e) => setInputVal(e.target.value)} placeholder="Add a task..." className="flex-1 h-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)" }} />
          <Button type="submit" className="btn-primary h-10 px-4"><Plus className="w-4 h-4" /></Button>
        </form>

        <div className="divide-y" style={{ borderColor: "var(--border)" }}>
          {active.length === 0 ? (
            <div className="flex flex-col items-center py-12">
              <ClipboardList className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} />
              <p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>No active tasks</p>
              <p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px" }}>Add a task above to get started</p>
            </div>
          ) : (
            active.map(t => {
              const due = fmtDue(t.due_date);
              const priority = t.priority || "normal";
              return (
                <div key={t.id} className="flex items-start gap-3 px-5 py-3 hover:bg-slate-50 transition-colors group">
                  {/* Priority dot */}
                  <button onClick={() => cyclePriority(t.id, priority)} className="w-3 h-3 rounded-full flex-shrink-0 mt-2 transition-colors cursor-pointer" style={{ background: PRIORITY_COLORS[priority] }} title={`Priority: ${priority} (click to change)`} />
                  {/* Complete checkbox */}
                  <button onClick={() => completeTodo(t.id)} className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-colors hover:border-green-400 hover:bg-green-50" style={{ borderColor: "var(--border)", background: "transparent" }} title="Mark complete">
                    <Check className="w-3 h-3 opacity-0 group-hover:opacity-30" style={{ color: "var(--success)" }} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <input defaultValue={capitalize(t.text)} onBlur={(e) => { if (e.target.value !== t.text) updateTodoText(t.id, e.target.value); }} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} className="text-sm w-full bg-transparent focus:outline-none focus:bg-white focus:ring-1 rounded px-1 -ml-1 transition-colors" style={{ color: "var(--text-primary)" }} />
                    <div className="flex items-center gap-2 mt-1">
                      {t.created_by && t.created_by !== assignedTo && (
                        <span className="text-[10px] font-medium" style={{ color: "var(--primary)" }}>Added by {t.created_by}</span>
                      )}
                      <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                        {t.due_date ? `Due: ${due.label}` : `Added ${new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}`}
                      </span>
                    </div>
                  </div>
                  {/* Due date badge + picker */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {due && due.tag && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: due.color === "#DC2626" ? "#FEE2E2" : due.color === "#D97706" ? "#FEF3C7" : "var(--bg)", color: due.color }}>{due.tag}</span>
                    )}
                    <div className="relative">
                      <input type="date" defaultValue={t.due_date ? new Date(t.due_date).toISOString().split("T")[0] : ""} onChange={(e) => { if (e.target.value) updateTodoDueDate(t.id, new Date(e.target.value).toISOString()); else updateTodoDueDate(t.id, null); }} className="w-7 h-7 opacity-0 absolute inset-0 cursor-pointer" title="Set due date" />
                      <div className="w-7 h-7 rounded flex items-center justify-center transition-colors" style={{ background: t.due_date ? (due.color === "#DC2626" ? "#FEE2E2" : due.color === "#D97706" ? "#FEF3C7" : "var(--bg)") : "transparent", border: `1px solid ${t.due_date ? due.color || "var(--border)" : "var(--border)"}` }}>
                        <CalendarDays className="w-3.5 h-3.5" style={{ color: t.due_date ? due.color : "var(--text-muted)" }} />
                      </div>
                    </div>
                  </div>
                  <button onClick={() => deleteTodo(t.id)} className="opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity flex-shrink-0" style={{ color: "var(--text-muted)" }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Completed section */}
        {completed.length > 0 && (
          <div style={{ borderTop: "1px solid var(--border)" }}>
            <button onClick={() => setShowCompleted(p => ({ ...p, [assignedTo]: !p[assignedTo] }))} className="w-full flex items-center gap-2 px-5 py-3 text-xs font-medium transition-colors hover:bg-slate-50" style={{ color: "var(--text-muted)" }}>
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              Show completed ({completed.length})
            </button>
            {isExpanded && (
              <div className="divide-y" style={{ borderColor: "var(--border)" }}>
                {completed.map(t => (
                  <div key={t.id} className="flex items-center gap-3 px-5 py-2.5 group" style={{ opacity: 0.6 }}>
                    <button onClick={() => uncompleteTodo(t.id)} className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "var(--success)", border: "none" }}>
                      <Check className="w-3 h-3 text-white" />
                    </button>
                    <span className="text-sm line-through flex-1" style={{ color: "var(--text-muted)" }}>{capitalize(t.text)}</span>
                    <button onClick={() => deleteTodo(t.id)} className="opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity" style={{ color: "var(--text-muted)" }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-8 pb-4"}>
        <h1 className={isMobile ? "text-lg font-semibold mb-4" : "ui-page-title mb-6"}>To-Do Lists</h1>
      </div>
      <div className={isMobile ? "px-4 pb-4 flex flex-col gap-4" : "px-8 pb-8 flex gap-6 flex-wrap"}>
        {ADMIN_USERS.map(name => {
          const userTodos = todos.filter(t => t.assigned_to === name);
          return renderList(userTodos, name, inputValues[name] || "", (v) => setInputValues(p => ({ ...p, [name]: v })));
        })}
      </div>
    </div>
  );
}
